import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Eye, EyeOff, ListFilter, Plus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isComboboxDismissal } from '@/components/ui/multi-id-combobox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { IncidentTypes } from 'components/IncidentConfig/IncidentTypes';
import { useAors } from 'hooks/useAors';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { IncidentListCard } from './IncidentListCard';
import { IncidentsFilterBar } from './IncidentsFilterBar';
import { isExerciseType } from './utils/isExerciseType';
import type { IncidentListItemData } from './IncidentListItem';
import { buildIncidentListItems } from './utils/buildIncidentListItems';
import { useOrgEvents } from 'hooks/orgEvents/orgEventHooks';
import { UpgradeHoverLock } from 'components/Notifications/LockedUpgradeOption';
import {
  useHubOnlyFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import { FOC_INCIDENTS_TOUR_PARAM } from '../../focIncidentsTour/focIncidentsTourSteps';

const Incidents: React.FC = () => {
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { data: orgEvents = [] } = useOrgEvents();
  const { isMapVisible, hiddenIncidentIds, setHiddenIncidentIds } =
    useUscgMapContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hubOnly = useHubOnlyFlag();
  const focRequirements = useUscgFocRequirementsFlag();
  const tourStartedRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedAorIds, setSelectedAorIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const wantsTour = searchParams.get(FOC_INCIDENTS_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!wantsTour) {
      tourStartedRef.current = false;
      return;
    }
    if (!focRequirements || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_INCIDENTS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../focIncidentsTour/focIncidentsTour')
        .then(({ startFocIncidentsTour }) => {
          startFocIncidentsTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [focRequirements, setSearchParams, wantsTour]);

  const typeOptions = useMemo(
    () =>
      IncidentTypes.filter((type) => !isExerciseType(type)).map((type) => ({
        value: type,
        label: type,
      })),
    []
  );

  const aorOptions = useMemo(
    () =>
      aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aors]
  );

  const aorNameById = useMemo(() => {
    return new Map(aors.map((aor) => [aor.id, aor.name]));
  }, [aors]);

  const eventNameById = useMemo(() => {
    return new Map(orgEvents.map((event) => [event.id, event.name]));
  }, [orgEvents]);

  const listIncidents = useMemo<IncidentListItemData[]>(
    () =>
      buildIncidentListItems({
        incidents,
        aorNameById,
        eventNameById,
      }),
    [incidents, aorNameById, eventNameById]
  );

  const filteredIncidents = useMemo<IncidentListItemData[]>(() => {
    return listIncidents.filter((incident) => {
      const matchesSearch = incident.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesType =
        selectedTypes.length === 0 ||
        incident.incidentTypes.some((type) => selectedTypes.includes(type));

      const matchesAor =
        selectedAorIds.length === 0 ||
        incident.aorIds.some((aorId) => selectedAorIds.includes(aorId));

      return matchesSearch && matchesType && matchesAor;
    });
  }, [listIncidents, searchTerm, selectedTypes, selectedAorIds]);

  const filteredIncidentIds = useMemo(
    () => filteredIncidents.map((incident) => incident.id),
    [filteredIncidents]
  );

  const hiddenFilteredIncidentIds = useMemo(
    () => filteredIncidentIds.filter((id) => hiddenIncidentIds.includes(id)),
    [filteredIncidentIds, hiddenIncidentIds]
  );

  const isAllVisible = useMemo(() => {
    if (filteredIncidentIds.length === 0) return false;
    return hiddenFilteredIncidentIds.length === 0;
  }, [filteredIncidentIds.length, hiddenFilteredIncidentIds.length]);

  const handleToggleAll = useCallback(() => {
    if (filteredIncidentIds.length === 0) return;

    const filteredSet = new Set(filteredIncidentIds);

    setHiddenIncidentIds((prevIds) => {
      if (isAllVisible) {
        return [...new Set([...prevIds, ...filteredIncidentIds])];
      }

      return prevIds.filter((id) => !filteredSet.has(id));
    });
  }, [filteredIncidentIds, isAllVisible, setHiddenIncidentIds]);

  const popoverFiltersActive =
    selectedTypes.length > 0 || selectedAorIds.length > 0;

  const appliedFilterChips = useMemo(() => {
    const chips: { id: string; label: string }[] = [];
    for (const type of selectedTypes) {
      chips.push({ id: `type:${type}`, label: type });
    }
    for (const aorId of selectedAorIds) {
      const name = aorNameById.get(aorId);
      if (name) chips.push({ id: `aor:${aorId}`, label: name });
    }
    return chips;
  }, [aorNameById, selectedAorIds, selectedTypes]);

  const dismissFilterChip = useCallback((chipId: string) => {
    if (chipId.startsWith('type:')) {
      const type = chipId.slice('type:'.length);
      setSelectedTypes((types) => types.filter((value) => value !== type));
      return;
    }
    if (chipId.startsWith('aor:')) {
      const aorId = chipId.slice('aor:'.length);
      setSelectedAorIds((ids) => ids.filter((id) => id !== aorId));
    }
  }, []);

  return (
    <div
      className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:bg-background tw:text-foreground"
      data-tour="incidents-workspace"
    >
      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <h2 className="tw:m-0 tw:shrink-0 tw:text-sm tw:font-semibold">
            Incidents
            <span className="tw:ml-1.5 tw:font-normal tw:text-muted-foreground">
              ({filteredIncidents.length} active)
            </span>
          </h2>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5">
            <Popover
              open={filtersOpen}
              onOpenChange={(open, eventDetails) => {
                const fromCombobox =
                  !open &&
                  (eventDetails.reason === 'outside-press' ||
                    eventDetails.reason === 'focus-out') &&
                  isComboboxDismissal(eventDetails.event);
                if (fromCombobox) {
                  eventDetails.cancel();
                  return;
                }
                setFiltersOpen(open);
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Filters"
                    aria-expanded={filtersOpen}
                    className="tw:relative"
                  >
                    <ListFilter className="tw:h-4 tw:w-4" />
                    {popoverFiltersActive ? (
                      <span
                        className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
                        aria-hidden
                      />
                    ) : null}
                  </Button>
                }
              />
              <PopoverContent align="end" className="dark tw:w-80 tw:p-3">
                <IncidentsFilterBar
                  typeOptions={typeOptions}
                  selectedTypes={selectedTypes}
                  onTypesChange={setSelectedTypes}
                  aorOptions={aorOptions}
                  selectedAorIds={selectedAorIds}
                  onAorIdsChange={setSelectedAorIds}
                />
              </PopoverContent>
            </Popover>
            <UpgradeHoverLock locked={hubOnly}>
              <Button
                type="button"
                size="sm"
                onClick={() => navigate('/create/incident')}
                className="tw:gap-1"
              >
                <Plus className="tw:h-4 tw:w-4" />
                New Incident
              </Button>
            </UpgradeHoverLock>
            {isMapVisible ? (
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                title={
                  isAllVisible
                    ? 'Hide all incidents from map'
                    : 'Show all incidents on map'
                }
                aria-label={
                  isAllVisible ? 'Hide all incidents' : 'Show all incidents'
                }
                onClick={handleToggleAll}
              >
                {isAllVisible ? (
                  <Eye className="tw:h-4 tw:w-4" />
                ) : (
                  <EyeOff className="tw:h-4 tw:w-4" />
                )}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="tw:relative tw:w-full tw:max-w-sm">
          <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search incidents…"
            className="tw:h-8 tw:pl-8 tw:text-xs"
            aria-label="Search incidents"
          />
        </div>
      </div>

      {appliedFilterChips.length > 0 ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          {appliedFilterChips.map((chip) => (
            <Badge
              key={chip.id}
              variant="outline"
              className="tw:h-6 tw:gap-1 tw:pr-1"
            >
              <span className="tw:max-w-[12rem] tw:truncate">{chip.label}</span>
              <button
                type="button"
                className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => dismissFilterChip(chip.id)}
              >
                <X className="tw:size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}

      <div
        className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:space-y-2 tw:overflow-x-hidden tw:overflow-y-auto"
        data-tour="incidents-list"
      >
        {filteredIncidents.map((incident, index) => (
          <IncidentListCard
            key={incident.id}
            incident={incident}
            defaultCollapsed
            showMapVisibilityToggle
            tourHost={index === 0}
          />
        ))}
      </div>
    </div>
  );
};

export default Incidents;
